import { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Header() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location.pathname]);

  const isActive = useCallback((path: string) => location.pathname === path, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg group-hover:bg-primary-600 transition-colors duration-200">
              <i className="ri-book-open-line text-lg text-background-50"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
              香香漫画
            </h1>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                isActive('/')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
              }`}
            >
              首页
            </Link>

            {/* Category Dropdown */}
            <div ref={categoryRef} className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isCategoryOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-2 animate-[fadeIn_0.15s_ease-out]">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base w-5 text-center`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                isActive('/about')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
              }`}
            >
              关于我们
            </Link>
          </nav>

          {/* Search + Mobile Toggle */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center bg-background-100 rounded-full px-4 py-2 border border-background-200/70 transition-all duration-200 hover:border-background-300/60 focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-100">
              <i className="ri-search-line text-sm text-foreground-400 mr-2"></i>
              <input
                type="text"
                placeholder="搜索漫画..."
                className="bg-transparent text-sm text-foreground-900 placeholder-foreground-400 outline-none w-36 lg:w-48"
              />
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-200"
              aria-label="菜单"
            >
              <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg animate-[fadeIn_0.15s_ease-out]">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap ${
                isActive('/') ? 'bg-primary-100 text-primary-700' : 'text-foreground-700'
              }`}
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap"
              >
                <i className={`${cat.icon} text-base w-5 text-center`}></i>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap ${
                isActive('/about') ? 'bg-primary-100 text-primary-700' : 'text-foreground-700'
              }`}
            >
              关于我们
            </Link>
            <div className="pt-2">
              <div className="flex items-center bg-background-100 rounded-full px-4 py-2.5 border border-background-200/70">
                <i className="ri-search-line text-sm text-foreground-400 mr-2"></i>
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="bg-transparent text-sm text-foreground-900 placeholder-foreground-400 outline-none w-full"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </header>
  );
}